import { getClientBuildVersion } from "@/lib/buildVersion";
import { devLog } from "@/lib/logger";

// Build validation utilities to ensure we're running the correct version
export class BuildValidator {
  // Lazy evaluation to ensure environment variables are fully loaded
  get expectedBuildSha(): string {
    return getClientBuildVersion();
  }

  // Helper function to normalize commit SHAs for comparison
  private normalizeSha(sha: string): string {
    if (!sha) return '';
    // Remove common prefixes (stg-, prd-, dev-) and extract just the commit part
    return sha.replace(/^(stg-|prd-|dev-)/g, '').replace(/-\d+$/, '').substring(0, 8);
  }

  async validateBuildConsistency() {
    const warnings: string[] = [];
    const clientSha = this.expectedBuildSha;

    // Check for invalid client SHA (unexpanded templates)
    const isInvalidClientSha = clientSha.includes('${') || clientSha === 'dev' || clientSha.length < 5;

    if (isInvalidClientSha) {
      console.warn(`[BuildValidator] Invalid client SHA detected: ${clientSha}`);
      // In dev/staging, this is not critical - just warn
      if (import.meta.env.VITE_ENV_LABEL !== 'PRD') {
        warnings.push(`Client build SHA invalid (${clientSha}) - ignoring in ${import.meta.env.VITE_ENV_LABEL}`);
        return { isValid: true, serverSha: undefined, clientSha, swSha: undefined, warnings };
      }
    }

    let serverSha: string | undefined;

    // Try runtime API first (most reliable for server SHA)
    try {
      const apiResponse = await fetch(`/api/build?t=${Date.now()}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' }
      });
      if (apiResponse.ok) {
        const apiData = await apiResponse.json().catch(() => null);
        if (apiData?.resolvedSha && apiData.resolvedSha !== 'unknown') {
          serverSha = apiData.resolvedSha;
          devLog(`[BuildValidator] Server runtime SHA: ${serverSha} (source: ${apiData.source || 'api'})`);
        }
      }
    } catch (e) {
      console.warn('[BuildValidator] API build check failed:', e);
    }

    // Fallback to meta file if API failed
    if (!serverSha) {
      try {
        const response = await fetch(`/meta/build.json?t=${Date.now()}`, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' }
        });
        if (response.ok) {
          const headerSha = response.headers.get('X-Build-Sha') || undefined;
          const meta = await response.json().catch(() => null);
          serverSha = headerSha || meta?.commit || meta?.sha || meta?.buildId || undefined;
          devLog(`[BuildValidator] Server meta SHA: ${serverSha} (source: meta/build.json)`);
        } else {
          warnings.push(`Build meta request failed (${response.status})`);
        }
      } catch {
        warnings.push('Failed to check server build SHA');
      }
    }

    // Compare SHAs
    if (serverSha && !isInvalidClientSha) {
      const normalizedServerSha = this.normalizeSha(serverSha);
      const normalizedClientSha = this.normalizeSha(clientSha);

      if (normalizedServerSha !== normalizedClientSha) {
        warnings.push(`Server build (${serverSha}) != Client (${clientSha}) [normalized: ${normalizedServerSha} vs ${normalizedClientSha}]`);
      }
    }

    let swSha: string | undefined;
    try {
      if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
        const { swUpdater } = await import("@/lib/swUpdater");
        swSha = await swUpdater.getBuildSha() || undefined;
        const normalizedSwSha = this.normalizeSha(swSha || '');
        const normalizedClientSha = this.normalizeSha(clientSha);

        // Only warn about SW SHA mismatch in production - SW is disabled by design in staging
        if (normalizedSwSha && normalizedSwSha !== normalizedClientSha && !isInvalidClientSha) {
          if (import.meta.env.VITE_ENV_LABEL === 'PRD') {
            warnings.push(`SW SHA (${swSha}) != Client (${clientSha}) [normalized: ${normalizedSwSha} vs ${normalizedClientSha}]`);
          } else {
            console.info(`[BuildValidator] SW SHA mismatch in ${import.meta.env.VITE_ENV_LABEL} - ignoring (SW disabled by design)`);
          }
        }
      } else if (import.meta.env.VITE_ENV_LABEL === 'PRD') {
        // Only warn about missing SW in production
        warnings.push("Service Worker not available or not active");
      }
    } catch {
      // Only report SW check failures in production
      if (import.meta.env.VITE_ENV_LABEL === 'PRD') {
        warnings.push("Failed to check SW build SHA");
      }
    }

    // Only consider actual mismatches as invalid, not network failures or invalid client SHA
    // Use normalized SHAs for final validation
    const normalizedServerSha = this.normalizeSha(serverSha || '');
    const normalizedClientSha = this.normalizeSha(clientSha);
    const normalizedSwSha = this.normalizeSha(swSha || '');

    const hasServerMismatch = Boolean(normalizedServerSha) && normalizedServerSha !== normalizedClientSha && !isInvalidClientSha;
    const hasSwMismatch = Boolean(normalizedSwSha) && normalizedSwSha !== normalizedClientSha && !isInvalidClientSha && import.meta.env.VITE_ENV_LABEL === 'PRD';
    
    return {
      isValid: !(hasServerMismatch || hasSwMismatch),
      serverSha,
      clientSha,
      swSha,
      warnings,
    };
  }
  
  logBuildInfo() {
    devLog("[BuildValidator] Build Info");
    devLog("VITE_BUILD_SHA", import.meta.env.VITE_BUILD_SHA);
    devLog("VITE_BUILD_TIME", import.meta.env.VITE_BUILD_TIME);
    devLog("VITE_ENV_LABEL", import.meta.env.VITE_ENV_LABEL);
    devLog("VITE_RUNTIME_ENV", import.meta.env.VITE_RUNTIME_ENV);
  }
  
  async diagnoseStaleCache(): Promise<{
    recommendations: string[];
    actions: (() => Promise<void>)[];
  }> {
    const recommendations: string[] = [];
    const actions: (() => Promise<void>)[] = [];
    
    // Check if we have stale assets
    const validation = await this.validateBuildConsistency();
    
    if (!validation.isValid) {
      recommendations.push('Build version mismatch detected');
      
      if (validation.serverSha !== validation.clientSha) {
        recommendations.push('Server has different build - hard refresh recommended');
        actions.push(async () => {
          window.location.reload();
        });
      }
      
      if (validation.swSha && validation.swSha !== validation.clientSha && import.meta.env.VITE_ENV_LABEL === 'PRD') {
        recommendations.push('Service Worker has stale build - force update recommended');
        actions.push(async () => {
          const { swUpdater } = await import('@/lib/swUpdater');
          await swUpdater.forceUpdate();
        });
      }
    }
    
    return { recommendations, actions };
  }
}

// Expose to window for debugging in production
declare global {
  interface Window {
    buildValidator: BuildValidator;
  }
}

export const buildValidator = new BuildValidator();
if (typeof window !== "undefined") (window as any).buildValidator = buildValidator;